import { createContext, ReactNode, useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { CommandHandlers, executeUiActionPlan } from '@/services/uiActions/executor'
import type {
  CommandResult,
  UiActionPageHandlers,
  UiActionPlan,
  UiActionPlanSource,
  UiActionRunnerStatus,
  UiTarget,
} from '@/services/uiActions/types'

type UiActionRunnerContextValue = {
  status: UiActionRunnerStatus
  results: CommandResult[]
  checkpoint: CommandResult | null
  enqueuePlan: (plan: UiActionPlan, source?: UiActionPlanSource) => Promise<UiActionRunnerStatus>
  clear: () => void
  cancel: () => void
  registerPageHandlers: (scope: 'v2_teammate', handlers: UiActionPageHandlers) => () => void
}

export const UiActionRunnerContext = createContext<UiActionRunnerContextValue | null>(null)

export function UiActionProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const pageHandlersRef = useRef(new Map<string, UiActionPageHandlers>())
  const cancelledRef = useRef(false)
  const [status, setStatus] = useState<UiActionRunnerStatus>('idle')
  const [results, setResults] = useState<CommandResult[]>([])
  const [checkpoint, setCheckpoint] = useState<CommandResult | null>(null)

  const registerPageHandlers = useCallback((scope: 'v2_teammate', handlers: UiActionPageHandlers) => {
    pageHandlersRef.current.set(scope, handlers)
    return () => {
      if (pageHandlersRef.current.get(scope) === handlers) pageHandlersRef.current.delete(scope)
    }
  }, [])

  const clear = useCallback(() => {
    setStatus('idle')
    setResults([])
    setCheckpoint(null)
    cancelledRef.current = false
  }, [])

  const cancel = useCallback(() => {
    cancelledRef.current = true
    setStatus('cancelled')
  }, [])

  const enqueuePlan = useCallback(
    async (plan: UiActionPlan) => {
      cancelledRef.current = false
      setStatus('running')
      setResults([])
      setCheckpoint(null)

      const handlers = createHandlers({
        navigate,
        getPageHandlers: () => pageHandlersRef.current.get('v2_teammate'),
        isCancelled: () => cancelledRef.current,
      })

      const nextResults = await executeUiActionPlan(plan, undefined, handlers)
      setResults(nextResults)

      const lastResult = nextResults.at(-1)
      if (cancelledRef.current) {
        setStatus('cancelled')
        return 'cancelled'
      } else if (lastResult?.status === 'checkpoint') {
        setCheckpoint(lastResult)
        setStatus('checkpoint')
        return 'checkpoint'
      } else if (lastResult?.status === 'error') {
        setStatus('error')
        return 'error'
      } else {
        setStatus('complete')
        return 'complete'
      }
    },
    [navigate]
  )

  const value = useMemo(
    () => ({
      status,
      results,
      checkpoint,
      enqueuePlan,
      clear,
      cancel,
      registerPageHandlers,
    }),
    [cancel, checkpoint, clear, enqueuePlan, registerPageHandlers, results, status]
  )

  return <UiActionRunnerContext.Provider value={value}>{children}</UiActionRunnerContext.Provider>
}

function createHandlers({
  navigate,
  getPageHandlers,
  isCancelled,
}: {
  navigate: ReturnType<typeof useNavigate>
  getPageHandlers: () => UiActionPageHandlers | undefined
  isCancelled: () => boolean
}): CommandHandlers<undefined> {
  const page = async () => waitForPageHandlers(getPageHandlers, isCancelled)

  return {
    navigate: async command => {
      if (!isAllowedRoute(command.route)) throw new Error(`Route is not allowed: ${command.route}`)
      navigate(command.route)
      await animationFrame()
      return { status: 'success', message: `Navigated to ${command.route}` }
    },
    wait_for: async command => {
      const path = pathTarget(command.target)
      const handlers = await waitForPath(path, getPageHandlers, isCancelled, command.timeout_ms)
      return { status: 'success', observation: handlers.observePath(path) }
    },
    observe: async command => ({ status: 'success', observation: (await page()).observePath(pathTarget(command.target)) }),
    focus: async command => ({ status: 'success', observation: await (await page()).focusPath(pathTarget(command.target)) }),
    set_value: async command => ({
      status: 'success',
      observation: await (await page()).setPathValue(pathTarget(command.target), command.value),
    }),
    select_option: async command => ({
      status: 'success',
      observation: await (await page()).selectPathOption(pathTarget(command.target), command.option),
    }),
    validate: async command => ({ status: 'success', observation: await (await page()).validatePath(pathTarget(command.scope)) }),
    click: async command => {
      const observation = await (await page()).clickTarget?.(command.target)
      return { status: 'success', message: 'Clicked target', observation }
    },
    type_text: () => {
      throw new Error('type_text is not implemented for the UI action POC yet; use set_value for form fields')
    },
    hitl_checkpoint: command => ({ status: 'checkpoint', message: command.message }),
    show_message: command => ({ status: 'success', message: command.message }),
  }
}

async function waitForPageHandlers(
  getPageHandlers: () => UiActionPageHandlers | undefined,
  isCancelled: () => boolean,
  timeoutMs = 5000
): Promise<UiActionPageHandlers> {
  const start = Date.now()

  while (Date.now() - start < timeoutMs) {
    if (isCancelled()) throw new Error('UI action plan was cancelled')
    const handlers = getPageHandlers()
    if (handlers) return handlers
    await animationFrame()
  }

  throw new Error('Timed out waiting for UI action page handlers')
}

async function waitForPath(
  path: string,
  getPageHandlers: () => UiActionPageHandlers | undefined,
  isCancelled: () => boolean,
  timeoutMs = 5000
): Promise<UiActionPageHandlers> {
  const start = Date.now()

  while (Date.now() - start < timeoutMs) {
    const handlers = await waitForPageHandlers(getPageHandlers, isCancelled, timeoutMs)
    try {
      handlers.observePath(path)
      return handlers
    } catch {
      await animationFrame()
    }
  }

  throw new Error(`Timed out waiting for path: ${path}`)
}

function pathTarget(target: UiTarget): string {
  if (target.kind !== 'path') throw new Error(`Expected path target, received ${target.kind}`)
  return target.path
}

function isAllowedRoute(route: string): boolean {
  return route === '/teammates/new' || /^\/teammates\/[^/]+(?:\/dialog)?$/.test(route)
}

function animationFrame(): Promise<void> {
  return new Promise(resolve => requestAnimationFrame(() => resolve()))
}
